/**
 * The grocery list for a week: generated from the planned meals, merged by
 * ingredient name + unit (2 onions + 3 onions = 5 onions), grouped in the aisle
 * order configured in D1, and checked off with a thumb while standing in the store.
 *
 * Hand-added items are marked and survive every rebuild.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Aisle, GroceryItem, GroceryList } from '../../shared/types';
import { weekLabel } from './dates';
import { errMsg, mealsApi, qtyLabel } from './mealsApi';
import {
  EmptyState,
  ErrorNote,
  Loading,
  WeekNav,
  btnGhost,
  btnPrimary,
  btnSecondary,
  cardCls,
  inputCls,
  labelCls,
} from './ui';

export default function GroceryPanel({
  week,
  aisles,
  onWeek,
  onGoToPlan,
  onGoToLibrary,
  onEditAisles,
}: {
  week: string;
  aisles: Aisle[];
  onWeek: (w: string) => void;
  onGoToPlan: () => void;
  onGoToLibrary: () => void;
  onEditAisles: () => void;
}) {
  const [list, setList] = useState<GroceryList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [rebuilding, setRebuilding] = useState(false);
  const [adding, setAdding] = useState(false);
  const [shared, setShared] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', quantity: '', unit: '', aisle_id: '' });

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await mealsApi.getGrocery(week));
    } catch (err) {
      setError(errMsg(err));
    }
  }, [week]);

  useEffect(() => {
    setList(null);
    void load();
  }, [load]);

  function markPending(id: number, on: boolean) {
    setPending((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  /** Optimistic — a checkbox that waits on a round trip feels broken in a store. */
  async function toggle(item: GroceryItem) {
    const checked = !item.checked;
    setList((prev) => patchItem(prev, item.id, { checked }));
    markPending(item.id, true);
    try {
      await mealsApi.updateGroceryItem(item.id, { checked });
      setError(null);
    } catch (err) {
      setList((prev) => patchItem(prev, item.id, { checked: item.checked }));
      setError(errMsg(err));
    } finally {
      markPending(item.id, false);
    }
  }

  async function removeItem(item: GroceryItem) {
    markPending(item.id, true);
    try {
      await mealsApi.removeGroceryItem(item.id);
      await load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      markPending(item.id, false);
    }
  }

  async function rebuild() {
    setRebuilding(true);
    setError(null);
    try {
      setList(await mealsApi.regenerate(week));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setRebuilding(false);
    }
  }

  /**
   * Hand the list to whoever is doing the shopping. navigator.share opens the OS share
   * sheet — Messages, Notes, whatever they use — which is why this sends plain text and
   * not a link: the person receiving it may not have the app, and a list you can read in
   * the message itself beats one you have to tap through to.
   *
   * Not a notification. The app still sends no SMS of its own; the phone does the sending.
   */
  async function shareList() {
    const text = listAsText(list, week);
    if (!text) return;
    try {
      if (navigator.share) {
        await navigator.share({ text });
        setShared(null);
        return;
      }
      await navigator.clipboard.writeText(text);
      setShared('Copied — paste it into Messages.');
      window.setTimeout(() => setShared(null), 4000);
    } catch (err) {
      // Dismissing the share sheet rejects. That is a choice, not a failure, so it must
      // not surface as an error banner.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(text);
        setShared('Copied — paste it into Messages.');
        window.setTimeout(() => setShared(null), 4000);
      } catch {
        setError("Couldn't share the list from this browser.");
      }
    }
  }

  async function addItem(e: React.FormEvent) {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    const quantity = form.quantity.trim() === '' ? null : Number(form.quantity);
    if (quantity !== null && !Number.isFinite(quantity)) {
      setError('Quantity needs to be a number (or left blank).');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      await mealsApi.addGroceryItem({
        week_start: week,
        name,
        quantity,
        unit: form.unit.trim() || null,
        aisle_id: form.aisle_id === '' ? null : Number(form.aisle_id),
      });
      setForm({ name: '', quantity: '', unit: '', aisle_id: form.aisle_id });
      await load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setAdding(false);
    }
  }

  const items = list?.groups.flatMap((g) => g.items) ?? [];
  const done = items.filter((i) => i.checked).length;
  const bare = list?.meals_missing_ingredients ?? [];
  const toGrab = items.length - done;

  return (
    <div className="space-y-4">
      <div className={`${cardCls} p-3`}>
        <WeekNav week={week} onWeek={onWeek} />
      </div>

      {error && <ErrorNote message={error} onRetry={load} />}
      {shared && (
        <p className="rounded-xl border border-sage/30 bg-sage-soft px-3 py-2 text-sm text-ink">
          {shared}
        </p>
      )}

      {list === null ? (
        <Loading label="Loading the grocery list" />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink">
                {items.length === 0
                  ? 'Nothing on the list yet'
                  : `${done} of ${items.length} in the cart`}
              </p>
              <p className="text-xs text-ink-faint">
                Built from this week's meals. Anything you add by hand stays put.
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" className={btnSecondary} onClick={onEditAisles}>
                Aisle order
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={rebuild}
                disabled={rebuilding}
              >
                {rebuilding ? 'Rebuilding…' : 'Rebuild from meals'}
              </button>
              <button
                type="button"
                className={btnPrimary}
                onClick={() => void shareList()}
                disabled={toGrab === 0}
                title={toGrab === 0 ? 'Nothing left to grab' : undefined}
              >
                Send the list
              </button>
            </div>
          </div>

          {items.length > 0 && (
            <div
              className="h-2 overflow-hidden rounded-full bg-surface-sunk"
              role="progressbar"
              aria-valuenow={done}
              aria-valuemin={0}
              aria-valuemax={items.length}
              aria-label="Items in the cart"
            >
              <div
                className="h-full rounded-full bg-sage transition-[width]"
                style={{ width: `${items.length === 0 ? 0 : (done / items.length) * 100}%` }}
              />
            </div>
          )}

          {/*
            A planned meal with no ingredients contributes nothing, and the list just
            comes up short — there is no error to notice, and "Rebuild from meals"
            returns an identical list and looks broken. Naming the meals is the whole
            difference between "this is wrong" and "this is what the week actually has".
          */}
          {bare.length > 0 && (
            <div className="rounded-xl border border-honey/30 bg-honey-soft p-3">
              <p className="text-sm font-medium text-ink">
                {bare.length === 1
                  ? '1 planned meal has no ingredients saved, so it added nothing here.'
                  : `${bare.length} planned meals have no ingredients saved, so they added nothing here.`}
              </p>
              <p className="mt-1 text-sm text-ink-soft">{bare.map((m) => m.name).join(', ')}</p>
              <button type="button" className={`${btnSecondary} mt-2`} onClick={onGoToLibrary}>
                Add their ingredients
              </button>
            </div>
          )}

          {items.length === 0 ? (
            /*
              "Plan a few meals" is bad advice when the week is already planned and the
              meals are simply bare — it sends the cook to the one screen that cannot
              fix it.
            */
            <EmptyState
              title="The list is empty"
              body={
                bare.length > 0
                  ? "This week is planned, but none of those meals have ingredients saved yet — so there is nothing to build a list from. Add them to the meals above, or add anything you need by hand below."
                  : 'Plan a few meals for this week and every ingredient lands here, merged and sorted by aisle. You can also add anything else you need below.'
              }
              action={
                bare.length > 0 ? (
                  <button type="button" className={btnPrimary} onClick={onGoToLibrary}>
                    Add their ingredients
                  </button>
                ) : (
                  <button type="button" className={btnPrimary} onClick={onGoToPlan}>
                    Plan this week's meals
                  </button>
                )
              }
            />
          ) : (
            <div className="space-y-4">
              {list.groups.map((group) => (
                <section key={group.aisle?.id ?? 'none'} className={`${cardCls} overflow-hidden`}>
                  <h3 className="border-b border-border bg-surface-sunk px-4 py-2 font-display text-sm font-semibold text-ink">
                    {group.aisle?.name ?? 'No aisle yet'}
                    <span className="ml-2 font-sans text-xs font-normal text-ink-faint">
                      {group.items.filter((i) => !i.checked).length} to grab
                    </span>
                  </h3>
                  <ul className="divide-y divide-border">
                    {group.items.map((item) => {
                      const qty = qtyLabel(item.quantity, item.unit);
                      return (
                        <li key={item.id} className="flex items-center gap-2 px-3">
                          <label className="flex min-h-[48px] flex-1 cursor-pointer items-center gap-3">
                            <input
                              type="checkbox"
                              checked={item.checked}
                              disabled={pending.has(item.id)}
                              onChange={() => toggle(item)}
                              className="size-5 shrink-0 rounded border-border text-sage accent-sage focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
                            />
                            <span
                              className={
                                item.checked
                                  ? 'text-sm text-ink-faint line-through'
                                  : 'text-sm text-ink'
                              }
                            >
                              {qty && <span className="font-medium">{qty} </span>}
                              {item.name}
                              {item.manual && (
                                <span className="ml-2 rounded-full bg-honey-soft px-2 py-0.5 text-xs font-medium text-honey">
                                  added
                                </span>
                              )}
                            </span>
                          </label>
                          {item.manual && (
                            <button
                              type="button"
                              className={btnGhost}
                              disabled={pending.has(item.id)}
                              aria-label={`Remove ${item.name} from the list`}
                              onClick={() => removeItem(item)}
                            >
                              <span aria-hidden="true">✕</span>
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}

          {/* hand-added items: manual = 1, so a rebuild never removes them */}
          <form onSubmit={addItem} className={`${cardCls} space-y-3 p-4`}>
            <h3 className="font-display text-sm font-semibold text-ink">Add something else</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_5rem_6rem]">
              <div className="col-span-2 sm:col-span-1">
                <label className="sr-only" htmlFor="add-name">
                  Item
                </label>
                <input
                  id="add-name"
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Paper towels"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="sr-only" htmlFor="add-qty">
                  Quantity
                </label>
                <input
                  id="add-qty"
                  className={inputCls}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  placeholder="2"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className="sr-only" htmlFor="add-unit">
                  Unit
                </label>
                <input
                  id="add-unit"
                  className={inputCls}
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  placeholder="rolls"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className={labelCls} htmlFor="add-aisle">
                  Aisle
                </label>
                <select
                  id="add-aisle"
                  className={inputCls}
                  value={form.aisle_id}
                  onChange={(e) => setForm({ ...form, aisle_id: e.target.value })}
                >
                  <option value="">No aisle</option>
                  {aisles.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className={btnPrimary} disabled={adding || !form.name.trim()}>
                {adding ? 'Adding…' : 'Add to list'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}

/**
 * The list as something readable in a text message: grouped by aisle, in store order,
 * and only what is still to grab — anything already in the cart is noise to the person
 * being asked to pick it up.
 */
function listAsText(list: GroceryList | null, week: string): string {
  if (!list) return '';
  const blocks: string[] = [];
  for (const group of list.groups) {
    const left = group.items.filter((i) => !i.checked);
    if (left.length === 0) continue;
    const lines = left.map((i) => {
      const qty = qtyLabel(i.quantity, i.unit);
      return `- ${qty ? `${qty} ` : ''}${i.name}`;
    });
    blocks.push(`${(group.aisle?.name ?? 'Everything else').toUpperCase()}\n${lines.join('\n')}`);
  }
  if (blocks.length === 0) return '';
  return `Groceries — ${weekLabel(week)}\n\n${blocks.join('\n\n')}`;
}

function patchItem(
  list: GroceryList | null,
  id: number,
  patch: Partial<GroceryItem>
): GroceryList | null {
  if (!list) return list;
  return {
    ...list,
    groups: list.groups.map((g) => ({
      ...g,
      items: g.items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    })),
  };
}
